import { useEffect, useRef, useCallback } from 'react'

/**
 * Hook to track if a component is currently mounted.
 * Returns a function that returns true if the component is still mounted.
 *
 * This is essential for preventing state updates on unmounted components
 * and avoiding Firestore ca9 assertion failures.
 *
 * @example
 * const isMounted = useMounted()
 *
 * const fetchData = async () => {
 *   const result = await someAsyncOperation()
 *   if (!isMounted()) return // Component unmounted
 *   setState(result)
 * }
 */
export const useMounted = (): (() => boolean) => {
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  return useCallback(() => mountedRef.current, [])
}
